import React, { useState, useEffect } from 'react';
import { DollarSign, Clock, CheckCircle, AlertTriangle, TrendingUp, Globe, Search, Download, Plus, Mail, CreditCard, BarChart3, RefreshCw, FileText, Eye, X, Filter } from 'lucide-react';

const PaymentsManagement = () => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');

  // Statistiques principales
  const stats = {
    totalVolumeSept: "€2.4M",
    transactionsSept: 87,
    growthPercent: 18,
    pendingAmount: "€485K",
    pendingCount: 12,
    completedAmount: "€1.8M",
    completedCount: 68,
    commissionsGenerated: "€312K",
    avgCommissionRate: 13,
    upcomingPayments: 5,
    upcomingAmount: "€234K",
    activeCurrencies: 3,
    currencies: ['EUR', 'USD', 'GBP']
  };

  // Transactions récentes
  const recentTransactions = [
    {
      id: 'PAY-2025-0947',
      clientInitials: 'JD',
      clientName: 'James Davidson',
      clientType: 'VIP Gold',
      property: 'Villa Azure Dreams',
      location: 'St-Barth',
      reservationId: '#PRV2587',
      type: 'Acompte 30%',
      amount: 84000,
      commission: 10920,
      method: 'SWIFT',
      methodDetail: 'Virement',
      date: '11 Sept 10:45',
      status: 'completed'
    },
    {
      id: 'PAY-2025-0946',
      clientInitials: 'SC',
      clientName: 'Sophie Chen',
      clientType: 'VIP Platinum',
      property: 'Yacht Serenity',
      location: 'Mykonos',
      reservationId: '#PRV2586',
      type: 'Solde',
      amount: 122500,
      commission: 18375,
      method: 'AMEX',
      methodDetail: 'Platinum',
      date: '11 Sept 09:20',
      status: 'processing'
    },
    {
      id: 'PAY-2025-0945',
      clientInitials: 'MA',
      clientName: 'Mohammed Al-Rashid',
      clientType: 'VIP Diamond',
      property: 'Villa Paradiso',
      location: 'St-Tropez',
      reservationId: '#PRV2585',
      type: 'Acompte 30%',
      amount: 135000,
      commission: 20250,
      method: 'SEPA',
      methodDetail: 'Virement',
      date: '10 Sept 16:30',
      status: 'pending'
    },
    {
      id: 'PAY-2025-0944',
      clientInitials: 'EW',
      clientName: 'Emma Williams',
      clientType: 'Nouveau',
      property: 'Villa Sunset',
      location: 'Sardaigne',
      reservationId: '#PRV2584',
      type: 'Services Extra',
      amount: 8500,
      commission: 1275,
      method: 'STRIPE',
      methodDetail: 'Carte',
      date: '10 Sept 14:15',
      status: 'completed'
    },
    {
      id: 'PAY-2025-0943',
      clientInitials: 'RH',
      clientName: 'Robert Hamilton',
      clientType: 'VIP Gold',
      property: 'Villa Celestial',
      location: 'Mykonos',
      reservationId: '#PRV2583',
      type: 'Remboursement',
      amount: -45000,
      commission: 0,
      method: 'SWIFT',
      methodDetail: 'Virement',
      date: '09 Sept 11:00',
      status: 'processed'
    }
  ];

  // Données du flux de trésorerie (7 derniers jours)
  const cashFlowData = [
    { date: '5 Sept', amount: 180000 },
    { date: '6 Sept', amount: 95000 },
    { date: '7 Sept', amount: 250000 },
    { date: '8 Sept', amount: 420000 },
    { date: '9 Sept', amount: 180000 },
    { date: '10 Sept', amount: 380000 },
    { date: '11 Sept', amount: 485000 }
  ];

  // Répartition des méthodes de paiement
  const paymentMethods = [
    { method: 'Virement SWIFT', percentage: 45, color: 'bg-blue-500' },
    { method: 'AMEX Platinum', percentage: 28, color: 'bg-green-500' },
    { method: 'SEPA', percentage: 18, color: 'bg-yellow-500' },
    { method: 'Stripe', percentage: 7, color: 'bg-purple-500' },
    { method: 'Crypto USDC', percentage: 2, color: 'bg-orange-500' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processed': return 'bg-gray-100 text-gray-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Complété';
      case 'processing': return 'En cours';
      case 'pending': return 'En attente';
      case 'processed': return 'Traité';
      case 'failed': return 'Échoué';
      default: return status;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Acompte 30%': return 'bg-blue-100 text-blue-800';
      case 'Solde': return 'bg-green-100 text-green-800';
      case 'Services Extra': return 'bg-purple-100 text-purple-800';
      case 'Remboursement': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const TransactionDetailsModal = () => {
    if (!showDetailsModal || !selectedTransaction) return null;

    const transaction = recentTransactions.find(t => t.id === selectedTransaction);
    if (!transaction) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800">Détails du Paiement</h3>
            <button
              onClick={() => setShowDetailsModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Information Transaction */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Information Transaction</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">ID Transaction</span>
                  <p className="font-medium">{transaction.id}</p>
                </div>
                <div>
                  <span className="text-gray-600">Date</span>
                  <p className="font-medium">{transaction.date}</p>
                </div>
                <div>
                  <span className="text-gray-600">Type</span>
                  <p className="font-medium">{transaction.type}</p>
                </div>
                <div>
                  <span className="text-gray-600">Statut</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                    {getStatusText(transaction.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Client */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Client</h4>
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  {transaction.clientInitials}
                </div>
                <div>
                  <p className="font-medium text-gray-800">{transaction.clientName}</p>
                  <p className="text-sm text-gray-600">{transaction.clientType} • Client depuis 2023</p>
                </div>
              </div>
              <div className="text-sm space-y-1">
                <p><span className="text-gray-600">Email:</span> <span className="font-medium">james.davidson@*****.com</span></p>
                <p><span className="text-gray-600">Téléphone:</span> <span className="font-medium">+1 (212) ***-****</span></p>
                <p><span className="text-gray-600">Historique:</span> <span className="font-medium">12 réservations • €3.2M total</span></p>
              </div>
            </div>

            {/* Réservation Associée */}
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Réservation Associée</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Propriété</span>
                  <p className="font-medium">{transaction.property}</p>
                </div>
                <div>
                  <span className="text-gray-600">Destination</span>
                  <p className="font-medium">{transaction.location}</p>
                </div>
                <div>
                  <span className="text-gray-600">Dates</span>
                  <p className="font-medium">15-29 Déc 2025</p>
                </div>
                <div>
                  <span className="text-gray-600">Montant Total</span>
                  <p className="font-medium">€280,000</p>
                </div>
              </div>
            </div>

            {/* Détails Financiers */}
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Détails Financiers</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Montant Encaissé</span>
                  <span className="font-medium">€{transaction.amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Commission PRIVIA (13%)</span>
                  <span className="font-medium text-green-600">€{transaction.commission.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Services additionnels</span>
                  <span className="font-medium">€2,100</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">Total Commission</span>
                  <span className="font-bold text-green-600">€{(transaction.commission + 2100).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reversement Propriétaire</span>
                  <span className="font-medium">€{(transaction.amount - transaction.commission - 2100).toLocaleString()}</span>
                </div>
                <div className="text-xs text-gray-500">Prévu le 15 Sept 2025</div>
              </div>
            </div>

            {/* Méthode de Paiement */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Méthode de Paiement</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Type</span>
                  <p className="font-medium">{transaction.method} {transaction.methodDetail}</p>
                </div>
                <div>
                  <span className="text-gray-600">Référence</span>
                  <p className="font-medium">SWIFT-2025-JD-0947</p>
                </div>
                <div>
                  <span className="text-gray-600">Banque Émettrice</span>
                  <p className="font-medium">JP Morgan Chase</p>
                </div>
                <div>
                  <span className="text-gray-600">Date Valeur</span>
                  <p className="font-medium">11 Sept 2025</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                <Mail size={16} />
                <span>Envoyer Reçu</span>
              </button>
              <button className="flex items-center space-x-2 px-4 py-2 text-green-600 border border-green-300 rounded-lg hover:bg-green-50">
                <FileText size={16} />
                <span>Générer Facture</span>
              </button>
              <button className="flex items-center space-x-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                <BarChart3 size={16} />
                <span>Voir Historique</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filteredTransactions = recentTransactions.filter(transaction => {
    const matchesStatus = filterStatus === 'all' || transaction.status === filterStatus;
    const matchesType = filterType === 'all' || transaction.type.toLowerCase().includes(filterType.toLowerCase());
    const matchesMethod = filterMethod === 'all' || transaction.method.toLowerCase().includes(filterMethod.toLowerCase());
    return matchesStatus && matchesType && matchesMethod;
  });

  return (
    <div className="payments-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des Paiements</h1>
            <p className="text-gray-600 mt-1">Vue complète des transactions et flux financiers</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Filter size={18} />
              <span>Filtres</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau Paiement</span>
            </button>
          </div>
        </div>

        {/* Statistiques principales */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-green-600" size={24} />
              <span className="text-sm font-medium text-green-600">+{stats.growthPercent}%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.totalVolumeSept}</div>
            <div className="text-sm text-gray-600">Volume Total (Sept 2025)</div>
            <div className="text-xs text-gray-500">{stats.transactionsSept} transactions</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-yellow-600" size={24} />
              <span className="text-sm font-medium text-yellow-600">Action requise</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.pendingAmount}</div>
            <div className="text-sm text-gray-600">En Attente</div>
            <div className="text-xs text-gray-500">{stats.pendingCount} paiements</div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-blue-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.completedAmount}</div>
            <div className="text-sm text-gray-600">Encaissés (30j)</div>
            <div className="text-xs text-gray-500">{stats.completedCount} transactions complétées</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="text-purple-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.commissionsGenerated}</div>
            <div className="text-sm text-gray-600">Commissions Générées</div>
            <div className="text-xs text-gray-500">Taux moyen: {stats.avgCommissionRate}%</div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-red-600" size={24} />
              <span className="text-sm font-medium text-red-600">Urgent</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.upcomingPayments}</div>
            <div className="text-sm text-gray-600">Échéances Proches</div>
            <div className="text-xs text-gray-500">{stats.upcomingAmount} dans les 7 jours</div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Globe className="text-teal-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.activeCurrencies}</div>
            <div className="text-sm text-gray-600">Multi-devises</div>
            <div className="text-xs text-gray-500">{stats.currencies.join(', ')} actives</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Actions Rapides */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Actions Rapides</h3>
          <div className="flex flex-wrap gap-3">
            <button className="flex items-center space-x-2 px-4 py-2 text-green-600 border border-green-300 rounded-lg hover:bg-green-50">
              <Plus size={18} />
              <span>Nouveau Paiement</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Mail size={18} />
              <span>Relances Auto</span>
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">5 clients</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
              <CreditCard size={18} />
              <span>Prélèvements</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50">
              <FileText size={18} />
              <span>Export Comptable</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-teal-600 border border-teal-300 rounded-lg hover:bg-teal-50">
              <RefreshCw size={18} />
              <span>Réconciliation</span>
              <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded-full">23 à vérifier</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-yellow-600 border border-yellow-300 rounded-lg hover:bg-yellow-50">
              <BarChart3 size={18} />
              <span>Rapport TVA</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Flux de Trésorerie */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Flux de Trésorerie (7 derniers jours)</h3>
            <div className="h-64 flex items-end space-x-2">
              {cashFlowData.map((day, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-blue-500 rounded-t"
                    style={{ height: `${(day.amount / 500000) * 100}%` }}
                  ></div>
                  <div className="text-xs text-gray-600 mt-2 transform -rotate-45">{day.date}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Répartition Méthodes de Paiement */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Répartition Méthodes de Paiement</h3>
            <div className="space-y-3">
              {paymentMethods.map((method, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 ${method.color} rounded-full`}></div>
                    <span className="text-sm text-gray-700">{method.method}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-800">{method.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="completed">Complété</option>
              <option value="processing">En cours</option>
              <option value="pending">En attente</option>
              <option value="failed">Échoué</option>
              <option value="processed">Remboursé</option>
            </select>

            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous types</option>
              <option value="acompte">Acompte (30%)</option>
              <option value="solde">Solde</option>
              <option value="commission">Commission</option>
              <option value="remboursement">Remboursement</option>
              <option value="extra">Extra services</option>
            </select>

            <select 
              value={filterMethod}
              onChange={(e) => setFilterMethod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes méthodes</option>
              <option value="swift">Virement SWIFT</option>
              <option value="sepa">Virement SEPA</option>
              <option value="amex">Carte bancaire</option>
              <option value="stripe">Amex Platinum</option>
              <option value="crypto">Crypto (USDC)</option>
            </select>

            <input
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
            <span className="text-sm text-gray-600">à</span>
            <input
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />

            <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Réinitialiser filtres
            </button>
          </div>
        </div>

        {/* Tableau des Transactions */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Transactions Récentes</h3>
              <div className="flex space-x-2">
                <button className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50">
                  Vue Détaillée
                </button>
                <button className="px-3 py-1 text-sm text-green-600 border border-green-300 rounded hover:bg-green-50">
                  Exporter
                </button>
                <button className="px-3 py-1 text-sm text-purple-600 border border-purple-300 rounded hover:bg-purple-50">
                  Synchroniser Stripe
                </button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Réservation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Méthode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-700 cursor-pointer">
                      {transaction.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {transaction.clientInitials}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{transaction.clientName}</div>
                          <div className="text-sm text-gray-500">{transaction.clientType}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.property}</div>
                      <div className="text-sm text-gray-500">{transaction.location} {transaction.reservationId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(transaction.type)}`}>
                        {transaction.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      €{Math.abs(transaction.amount).toLocaleString()}
                      {transaction.amount < 0 && <span className="text-red-600"> (remb.)</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {transaction.commission > 0 ? `€${transaction.commission.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.method}</div>
                      <div className="text-sm text-gray-500">{transaction.methodDetail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                        {getStatusText(transaction.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedTransaction(transaction.id);
                          setShowDetailsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Détails
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <TransactionDetailsModal />
    </div>
  );
};

export default PaymentsManagement;